<template>
    <div ref="touristForm" class="tourist-form d-flex flex-column">
        <slot name="label" :hide-index="hideIndex" :is-child="isChild" :bed-type="bedType">
            <div v-if="!hideIndex" class="tourist-form__label text-subtitle-1 text-capitalize font-weight-bold">
                {{ `${$t(label, 1)} ${index + 1} ${isChild ? `(${$t('child')})` : ''}` }}
            </div>
        </slot>
        <v-row v-if="showTouristSelector" class="mb-1">
            <v-col cols="12" md="6">
                <tourist-autocomplete
                    :person-id="tourist.personId"
                    :company-id="companyId"
                    :tourist="tourist"
                    :selected-tourists="selectedTourists"
                    :required="corporatePolicyViolation"
                    :disabled="disabled"
                    :default-entries="orderTouristPersons"
                    :first-name-localized="firstNameLocalized"
                    :last-name-localized="lastNameLocalized"
                    can-update
                    :disable-search="!isCorporate"
                    :is-corporate="isCorporate"
                    @change="personSelected"
                />
            </v-col>
        </v-row>
        <v-form ref="form" v-model="valid" data-cy="booking-form" :disabled="disabled">
            <div v-if="isLocalesFields && isCorporate" class="mb-6">
                <LocaleSwitcher :disabled="disabled" @change="changeActiveLangCode" />
            </div>
            <v-row class="tourist-form__form-wrapper" :no-gutters="isMobile" dense>
                <v-col v-if="!isFieldDisabled('PREFIX')" class="tourist-form__form-prefix" cols="4" md="2" xxl="1">
                    <v-select
                        v-model="tourist.prefix"
                        :items="['Mr', 'Ms', 'Mrs']"
                        :label="fieldLabel('salutation', 'PREFIX')"
                        :rules="[requiredRule('salutation', 'PREFIX')]"
                        persistent-placeholder
                        data-cy="tourist-field-prefix"
                        :validate-on="validateOn"
                        clearable
                        variant="outlined"
                        density="comfortable"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                    />
                </v-col>
                <v-col :cols="nameFieldCols.cols" :md="nameFieldCols.md" :lg="nameFieldCols.lg">
                    <TextFieldLocale
                        v-if="isLocalesFields && isCorporate"
                        v-model="firstNameLocalized[activeLangCode]"
                        :label="fieldLabel('first_name', nameNotRequired ? 'NAME' : undefined)"
                        :active-lang-code="activeLangCode"
                        :rules="
                            validationRules('first_name', [nameNotRequired ? () => true : requiredRule], null, {
                                citizenshipId: tourist.citizenshipId,
                                countryId,
                            })
                        "
                        :disabled="disablePrivateClientName || disabled"
                        data-cy="tourist-field-first-name-localized"
                        :validate-on="validateOn"
                        variant="outlined"
                        density="comfortable"
                        autocomplete="one-time-code"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                        @update:model-value="updateFirstNameL"
                    />
                    <v-text-field
                        v-else
                        v-model="firstName"
                        :label="fieldLabel('first_name', nameNotRequired ? 'NAME' : undefined)"
                        :rules="
                            validationRules(
                                'first_name',
                                [latinRule, nameNotRequired ? () => true : requiredRule],
                                null,
                                {citizenshipId: tourist.citizenshipId, countryId}
                            )
                        "
                        :disabled="disablePrivateClientName || disabled"
                        data-cy="tourist-field-first-name"
                        :validate-on="validateOn"
                        variant="outlined"
                        density="comfortable"
                        autocomplete="one-time-code"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                    />
                </v-col>
                <v-col cols="12" md="4" lg="2">
                    <TextFieldLocale
                        v-if="isLocalesFields && isCorporate"
                        v-model="lastNameLocalized[activeLangCode]"
                        :label="fieldLabel('last_name', nameNotRequired ? 'NAME' : undefined)"
                        :active-lang-code="activeLangCode"
                        :rules="
                            validationRules('last_name', [nameNotRequired ? () => true : requiredRule], null, {
                                citizenshipId: tourist.citizenshipId,
                                countryId,
                            })
                        "
                        :disabled="disablePrivateClientName || disabled"
                        data-cy="tourist-field-last-name-localized"
                        :validate-on="validateOn"
                        variant="outlined"
                        density="comfortable"
                        autocomplete="one-time-code"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                        @update:model-value="updateLastNameL"
                    />
                    <v-text-field
                        v-else
                        v-model="lastName"
                        :label="fieldLabel('last_name', nameNotRequired ? 'NAME' : undefined)"
                        :rules="
                            validationRules(
                                'last_name',
                                [latinRule, nameNotRequired ? () => true : requiredRule],
                                null,
                                {citizenshipId: tourist.citizenshipId, countryId}
                            )
                        "
                        :disabled="disablePrivateClientName || disabled"
                        data-cy="tourist-field-last-name"
                        :validate-on="validateOn"
                        variant="outlined"
                        density="comfortable"
                        autocomplete="one-time-code"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                    />
                </v-col>
                <v-col v-if="!isFieldDisabled('BIRTHDATE')" cols="12" md="4" lg="3" xl="2">
                    <date-picker
                        v-model="tourist.birthdate"
                        :label="fieldLabel('birthdate', 'BIRTHDATE')"
                        :rules="birthdateRules"
                        :min="minBirthDate"
                        :max="maxBirthDate"
                        year-active-picker
                        :validate-on="validateOn"
                        :disabled="disabled"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                    />
                </v-col>
                <v-col v-if="!disableCitizenship && !isFieldDisabled('CITIZENSHIP')" cols="12" md="4" lg="3" xl="2">
                    <v-autocomplete
                        v-model="tourist.citizenshipId"
                        :label="fieldLabel('citizenship', 'CITIZENSHIP')"
                        :items="countries"
                        item-title="name"
                        item-value="id"
                        :disabled="persistentCitizenship || disabled"
                        :rules="[requiredRule('citizenship', 'CITIZENSHIP')]"
                        data-cy="tourist-field-citizenship"
                        :validate-on="validateOn"
                        variant="outlined"
                        density="comfortable"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                    />
                </v-col>
                <slot name="beforeContacts" />
                <v-col v-if="!isFieldDisabled('EMAIL')" cols="12" md="4" lg="2">
                    <v-text-field
                        v-model="tourist.email"
                        :label="fieldLabel('email', 'EMAIL')"
                        type="email"
                        :rules="emailRules"
                        data-cy="tourist-field-email"
                        :validate-on="validateOn"
                        :disabled="hideContactData || disabled"
                        variant="outlined"
                        density="comfortable"
                        autocomplete="one-time-code"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                    />
                </v-col>
                <v-col v-if="!isFieldDisabled('PHONE')" cols="12" md="4" lg="3" xl="2">
                    <phone-field
                        v-model="tourist.phone"
                        :disabled="hideContactData || disabled"
                        :label="$t('phone')"
                        data-cy="tourist-field-phone"
                        :required="!disableRequiredRule && isFieldMandatory('PHONE')"
                        :validate-on="validateOn"
                        @blur="validateOnSubmit ? $refs.form.resetValidation() : null"
                    />
                </v-col>
                <slot :tourist="tourist" :required-rule="requiredRule" :field-label="fieldLabel" />
            </v-row>
        </v-form>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import TouristAutocomplete from '@/components/parts/Booking/TouristAutocomplete.vue'
    import {BookingFieldsMixin} from '@/components/booking/mixins/BookingFieldsMixin.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import PhoneField from '@/components/snippets/forms/PhoneField.vue'
    import getOrderTravelers from '@/adapters/orderTravelersAdapter'
    import personInfoAdapter from '@/adapters/personInfoAdapter'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'

    @Component({
        components: {
            TextFieldLocale,
            PhoneField,
            TouristAutocomplete,
            DatePicker,
        },
    })
    export class TouristForm extends mixins(FormValidationRulesMixin, BookingFieldsMixin) {
        @VModel() tourist
        @Prop({default: 'guest'}) label
        @Prop({required: true}) index
        @Prop() disabled
        @Prop() corporatePolicyViolation
        @Prop({default: false, type: Boolean}) hideIndex
        @Prop({default: false, type: Boolean}) disablePrivateClientName
        @Prop({default: false, type: Boolean}) hideContactData
        @Prop({default: false, type: Boolean}) disableCitizenship
        @Prop({default: () => []}) selectedTourists
        @Prop() startDate
        @Prop() endDate
        @Prop() countryId
        @Prop({default: false, type: Boolean}) nameNotRequired
        @Prop({default: false, type: Boolean}) fullWidth
        @Prop({default: false}) _valid

        activeLangCode = 'en'
        firstNameLocalized = {}
        lastNameLocalized = {}
        isLocalesFields = false

        mounted() {
            this.activeLangCode = this.locale
        }

        @Emit()
        changeValid(val) {
            return val
        }

        @Watch('locale')
        onLocaleChange() {
            this.$refs.form.validate()
        }

        @Emit()
        personSelected(person) {
            const index = this.index
            if (!person?.personId) {
                this.resetCorporateTourist()
            }
            if (!person) {
                return {person: {}, index}
            }
            const isCorporateUser = person.role && person.role?.includes('corp.')
            if (isCorporateUser) {
                this.firstNameLocalized = clone(person.firstNameLocalized) || {}
                this.lastNameLocalized = clone(person.lastNameLocalized) || {}
                this.isLocalesFields = person.personId
            }
            return {person, index}
        }

        resetCorporateTourist() {
            this.lastNameLocalized = {}
            this.firstNameLocalized = {}
            this.isLocalesFields = false
        }

        updateFirstNameL(val) {
            this.tourist.firstName = val
        }
        updateLastNameL(val) {
            this.tourist.lastName = val
        }

        changeActiveLangCode(code: string) {
            this.activeLangCode = code
        }

        get firstName() {
            return this.tourist?.firstName
        }

        set firstName(val) {
            this.tourist.firstName = val
        }

        get lastName() {
            return this.tourist?.lastName
        }

        set lastName(val) {
            this.tourist.lastName = val
        }

        get validateOnSubmit() {
            return true
        }

        get validateOn() {
            return this.validateOnSubmit ? 'submit' : 'blur'
        }

        get birthdateRules() {
            const rules = [this.birthdateAdultRule]

            if (this.isFieldMandatory('BIRTHDATE')) {
                rules.push(this.requiredRule('birthdate', 'BIRTHDATE'))
            }

            return rules
        }

        get birthdateAdultRule() {
            if (this.tourist.type === 'adult' && this.endDate) {
                const birthday = new Date(this.tourist.birthdate)
                const ageDifMs = new Date(this.endDate).getTime() - birthday.getTime()
                const ageDate = new Date(ageDifMs)

                const ageByTheEndOfService = Math.abs(ageDate.getUTCFullYear() - 1970)

                return () => ageByTheEndOfService >= 12 || this.$t('adult_birthdate_validation')
            } else {
                return () => true
            }
        }

        get emailRules() {
            const rules = [this.emailRule('email')]
            if (this.isFieldMandatory('EMAIL')) {
                rules.push(this.requiredRule('email', 'EMAIL'))
            }
            return rules
        }

        get locale() {
            return this.$i18n.locale
        }

        get countries() {
            return runtimeStore.countries
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get showTouristSelector() {
            return (
                (this.isCorporate && !this.isChild) ||
                (persistentStore.selectedOrderId && this.orderTouristPersons.length > 0)
            )
        }

        get isCorporate() {
            // TODO Replace companyType instead of takeClientMethod
            return (
                (authStore.person && ['corp.trip_coordinator', 'corp.director'].includes(authStore.person.role)) ||
                persistentStore.selectedCompanyAndAgent?.takeClientMethod === 'persons'
            )
        }

        get nameFieldCols() {
            return {
                cols: 12,
                md: 4,
                lg: 2,
            }
        }

        get persistentCitizenship() {
            return false
        }

        get bedType() {
            if (this.tourist.onExtrabed) return 'onExtraBed'
            else if (this.tourist.onWithoutPlace) return 'onWithoutPlace'

            return null
        }

        get orderTouristPersons() {
            return persistentStore.selectedOrderId && runtimeStore.orderDetails.services
                ? getOrderTravelers(runtimeStore.orderDetails)
                      .map(traveler => ({
                          ...personInfoAdapter(traveler),
                          age:
                              traveler.age ||
                              (personInfoAdapter(traveler).birthdate
                                  ? this.$dateFns.differenceInYears(
                                        new Date(),
                                        this.$dateFns.parseISO(personInfoAdapter(traveler).birthdate)
                                    )
                                  : null),
                      }))
                      .filter(
                          ({firstName, lastName, type, age}) =>
                              !!firstName &&
                              !!lastName &&
                              type === this.tourist.type &&
                              (type !== 'child' || !age || age === this.tourist.age)
                      )
                : []
        }

        // TODO Need refactoring with same in BookingPageBase
        get companyId() {
            return persistentStore.selectedCompanyAndAgent?.company.id || authStore.person?.companyId
        }

        get valid() {
            return this._valid
        }

        set valid(val) {
            this.changeValid(val)
        }
    }

    export default TouristForm
</script>
